import axios, { type AxiosError } from 'axios'
import RequestHandler from '../lib/request_handler'
import type {
  ApiError,
  CreatePostWithOnlySuccessStatusResponse,
  DeletePostResponse,
  NewPostFormResponse,
  PaginatedPostsResponse,
  PaginationParams,
  PostReaction,
  ReactionType,
  Shop,
  Theme,
  ThemeDetailResponse,
  ThemesResponse,
} from './types'

const API_BASE_URL = '/webview/message_board'

// CSRFトークンの設定（RequestHandlerを使用）
const token = RequestHandler.getToken()
axios.defaults.headers.common['X-CSRF-Token'] = token

export const fetchShopByQuery = async (query: string): Promise<Shop[]> => {
  try {
    const response = await axios.get<Shop[]>(`${API_BASE_URL}/posts/shop_by_query.json`, {
      params: { query },
    })
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const fetchThemes = async (): Promise<Theme[]> => {
  try {
    const response = await axios.get<ThemesResponse>(`${API_BASE_URL}/themes.json`)
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const fetchTheme = async (id: number): Promise<ThemeDetailResponse> => {
  try {
    const response = await axios.get<ThemeDetailResponse>(`${API_BASE_URL}/themes/${id}.json`)
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const fetchThemePosts = async (
  id: number,
  params: PaginationParams
): Promise<PaginatedPostsResponse> => {
  try {
    const response = await axios.get<PaginatedPostsResponse>(
      `${API_BASE_URL}/themes/${id}/posts.json`,
      { params }
    )
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const fetchUserPosts = async (params: PaginationParams): Promise<PaginatedPostsResponse> => {
  try {
    const response = await axios.get<PaginatedPostsResponse>(`${API_BASE_URL}/mypage.json`, {
      params,
    })
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const fetchNewPostData = async (): Promise<NewPostFormResponse> => {
  try {
    const response = await axios.get<NewPostFormResponse>(`${API_BASE_URL}/posts/new.json`)
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const createPostWithOnlySuccessStatus = async (postData: FormData): Promise<CreatePostWithOnlySuccessStatusResponse> => {
  try {
    const response = await axios.post<CreatePostWithOnlySuccessStatusResponse>(`${API_BASE_URL}/posts`, postData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    if (response.data.success) {
      return { success: true, error: response.data.error }
    } else {
      return { success: false, error: response.data.error }
    }
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const deletePost = async (id: number): Promise<DeletePostResponse> => {
  try {
    const response = await axios.delete<DeletePostResponse>(`${API_BASE_URL}/posts/${id}`)
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

// リアクション関連のAPI
export interface ReactionResponse {
  reactions: PostReaction[]
  reaction_types: ReactionType[]
  user_reactions: PostReaction[]
}

export const fetchPostReactions = async (postId: number): Promise<ReactionResponse> => {
  try {
    const response = await axios.get<ReactionResponse>(`${API_BASE_URL}/posts/${postId}/reactions`)
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const addReaction = async (
  postId: number,
  reactionTypeId: number
): Promise<PostReaction> => {
  try {
    const response = await axios.post<PostReaction>(`${API_BASE_URL}/posts/${postId}/reactions`, {
      reaction: { bargain_message_board_reaction_type_id: reactionTypeId },
    })
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const removeReaction = async (
  postId: number,
  reactionId: number
): Promise<{ success: boolean }> => {
  try {
    const response = await axios.delete<{ success: boolean }>(
      `${API_BASE_URL}/posts/${postId}/reactions/${reactionId}`
    )
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}

export const fetchPosts = async (params: PaginationParams): Promise<PaginatedPostsResponse> => {
  try {
    const response = await axios.get<PaginatedPostsResponse>(`${API_BASE_URL}/posts.json`, {
      params,
    })
    return response.data
  } catch (error: unknown) {
    const apiError = error as AxiosError<ApiError>
    throw apiError
  }
}
